import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  /** Optional display formatter for option labels. */
  formatOption?: (value: string) => string;
  disabled?: boolean;
}

/**
 * Compact multi-select dropdown for the fleet filter bar. Built on a plain
 * button + panel with click-outside handling — no extra Radix dependency.
 */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  formatOption = (v) => v,
  disabled = false,
}: MultiSelectProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    function onPointerDown(event: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);

    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  function toggle(value: string): void {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-9 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm',
          'hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50',
          selected.length > 0 && 'border-primary/40 bg-accent',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {label}
        {selected.length > 0 && (
          <span className="rounded bg-primary px-1.5 text-xs text-primary-foreground tabular-nums">
            {selected.length}
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5 opacity-50" aria-hidden />
      </button>

      {open && (
        <div
          className="absolute z-20 mt-1 max-h-72 w-52 overflow-auto rounded-md border bg-popover p-1 shadow-md"
          role="listbox"
        >
          {options.length === 0 && (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">No options</p>
          )}
          {options.map((value) => {
            const checked = selected.includes(value);

            return (
              <button
                key={value}
                type="button"
                role="option"
                aria-selected={checked}
                onClick={() => toggle(value)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
              >
                <span
                  className={cn(
                    'flex h-4 w-4 items-center justify-center rounded border',
                    checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                  )}
                >
                  {checked && <Check className="h-3 w-3" aria-hidden />}
                </span>
                <span className="capitalize">{formatOption(value)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
